// ======================================================================
// FILE: src/modules/wms/checklist/components/ChecklistSectionsTab.js
// MODULE: Work Management System (WMS)
// FEATURE: Checklist Management
//
// PURPOSE:
//   Manage Checklist Sections
//
// DEPENDENCIES:
//   wmsChecklistService
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Multi Tenant
//   ✓ Multi Location
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsChecklistService
from "../../services/wmsChecklistService";

import ChecklistItemsTab
from "./ChecklistItemsTab";

const DEFAULT_FORM = {

  sequence_no: "",

  section_code: "",
  section_name: "",

  section_weightage: "",

  description: "",
  remarks: "",
};

export default function ChecklistSectionsTab({
  checklistId,
}) {

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [showModal, setShowModal] =
    useState(false);

  const [editingSection, setEditingSection] =
    useState(null);

  const [selectedSection, setSelectedSection] =
  useState(null);


  const [form, setForm] =
    useState(DEFAULT_FORM);

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    if (checklistId) {

      loadSections();
    }

  }, [checklistId]);

  async function loadSections() {

    try {

      setLoading(true);

      const data =
        await wmsChecklistService
          .getSections(
            checklistId
          );

      setRows(data || []);

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load sections"
      );

    } finally {

      setLoading(false);
    }
  }

  // ==========================================================
  // ADD
  // ==========================================================

  function handleAdd() {

    setEditingSection(null);

    setForm(
      DEFAULT_FORM
    );

    setShowModal(true);
  }

  // ==========================================================
  // EDIT
  // ==========================================================

  function handleEdit(row) {

    setEditingSection(row);

    setForm({

      sequence_no:
        row.sequence_no || "",

      section_code:
        row.section_code || "",

      section_name:
        row.section_name || "",

      section_weightage:
        row.section_weightage || "",

      description:
        row.description || "",

      remarks:
        row.remarks || "",
    });

    setShowModal(true);
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!form.sequence_no) {

      alert(
        "Sequence No is required"
      );

      return;
    }

    if (!form.section_code) {

      alert(
        "Section Code is required"
      );

      return;
    }

    if (!form.section_name) {

      alert(
        "Section Name is required"
      );

      return;
    }

    try {

      if (editingSection) {

        await wmsChecklistService
          .updateSection(
            checklistId,
            editingSection.id,
            form
          );

      } else {

        await wmsChecklistService
          .createSection(
            checklistId,
            form
          );
      }

      setShowModal(false);

      await loadSections();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );
    }
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete Section ${row.section_name}?`
    );

    if (!ok) {
      return;
    }

    try {

      await wmsChecklistService
        .deleteSection(
          checklistId,
          row.id
        );

      await loadSections();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div className="card mt-3">

      <div
        className="
          card-header
          d-flex
          justify-content-between
          align-items-center
        "
      >

        <strong>
          Checklist Sections
        </strong>

        <button
          className="btn btn-sm btn-primary"
          onClick={handleAdd}
        >
          <i className="fas fa-plus me-1" />
          Add Section
        </button>

      </div>

      <div className="card-body p-0">

        <table
          className="
            table
            table-striped
            table-hover
            mb-0
          "
        >

          <thead>

            <tr>

              <th>Seq</th>

              <th>Section Code</th>

              <th>Section Name</th>

              <th>Weightage</th>

              <th width="260">
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {loading && (

              <tr>

                <td
                  colSpan="5"
                  className="text-center"
                >
                  Loading...
                </td>

              </tr>

            )}

            {!loading &&
              rows.length === 0 && (

              <tr>

                <td
                  colSpan="5"
                  className="text-center"
                >
                  No Records Found
                </td>

              </tr>

            )}

            {!loading &&
              rows.map((row) => (

              <tr key={row.id}>

                <td>
                  {row.sequence_no}
                </td>

                <td>
                  {row.section_code}
                </td>

                <td>
                  {row.section_name}
                </td>

                <td>
                  {row.section_weightage}
                </td>

                <td>

                <button
                    className="
                        btn
                        btn-sm
                        btn-info
                        me-1
                    "
                    onClick={() =>
                        setSelectedSection(row)
                    }
                    >
                    Items
                    </button>

                    <button
                    className="
                        btn
                        btn-sm
                        btn-primary
                        me-1
                    "
                    onClick={() =>
                        handleEdit(row)
                    }
                    >
                    Edit
                    </button>

                    <button
                    className="
                        btn
                        btn-sm
                        btn-danger
                    "
                    onClick={() =>
                        handleDelete(row)
                    }
                    >
                    Delete
                    </button>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {/* ==================================================== */}
      {/* MODAL */}
      {/* ==================================================== */}

      {showModal && (

        <div
          className="modal d-block"
          tabIndex="-1"
        >

          <div className="modal-dialog modal-lg">

            <div className="modal-content">

              <div className="modal-header">

                <h5>

                  {editingSection
                    ? "Edit Section"
                    : "Add Section"}

                </h5>

              </div>

              <div className="modal-body">

                <div className="row">

                  <div className="col-md-3 mb-3">

                    <label>
                      Sequence No *
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={form.sequence_no}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          sequence_no:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-3 mb-3">

                    <label>
                      Section Code *
                    </label>

                    <input
                      className="form-control"
                      value={form.section_code}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          section_code:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-6 mb-3">

                    <label>
                      Section Name *
                    </label>

                    <input
                      className="form-control"
                      value={form.section_name}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          section_name:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-4 mb-3">

                    <label>
                      Weightage
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={
                        form.section_weightage
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          section_weightage:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-12 mb-3">

                    <label>
                      Description
                    </label>

                    <textarea
                      rows="3"
                      className="form-control"
                      value={
                        form.description
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          description:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-12 mb-3">

                    <label>
                      Remarks
                    </label>

                    <textarea
                      rows="2"
                      className="form-control"
                      value={
                        form.remarks
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          remarks:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                </div>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowModal(false)
                  }
                >
                  Cancel
                </button>

              </div>

            </div>

          </div>

        </div>

            )}

      {selectedSection && (

        <ChecklistItemsTab
          sectionId={
            selectedSection.id
          }
          sectionName={
            selectedSection.section_name
          }
        />

      )}

    </div>

  );
}